import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { Plus, Copy } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { bobAnalyseService } from '@/lib/services/bobAnalyseService'
import { formatDateTime } from '@/lib/utils/timeFormat'
import type { BobAnalyseFormData, BobAnalyse } from '@/lib/services/bobAnalyseService'

interface BobAnalyseNewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
  onAnalyseCreated?: (bobId: string) => void
  existingBobAnalyses?: BobAnalyse[]
}

type CreationMode = 'empty' | 'copy'

export function BobAnalyseNewDialog({
  open,
  onOpenChange,
  onSuccess,
  onAnalyseCreated,
  existingBobAnalyses = [],
}: BobAnalyseNewDialogProps) {
  const { t } = useTranslation()
  const [creationMode, setCreationMode] = React.useState<CreationMode>('empty')
  const [selectedBobToCopy, setSelectedBobToCopy] = React.useState<string>('')

  const createBobAnalyseMutation = useMutation({
    mutationFn: (data: BobAnalyseFormData) => bobAnalyseService.createBobAnalyse(data),
    onSuccess: async response => {
      // Close the dialog immediately
      onOpenChange(false)

      // Notify parent of the created analyse ID so it can be selected
      if (onAnalyseCreated) {
        onAnalyseCreated(response.id)
      }

      // Call onSuccess immediately for empty/copy modes (no background processing needed)
      if (onSuccess) onSuccess()
    },
    onError: error => {
      console.error('Error creating BOB analysis:', error)
    },
  })

  const handleCreateBob = () => {
    if (creationMode === 'copy' && !selectedBobToCopy) {
      console.error('Please select a BOB analysis to copy')
      return
    }

    let data: BobAnalyseFormData = {}

    if (creationMode === 'copy') {
      // Find the selected BOB analysis to copy
      const bobToCopy = existingBobAnalyses.find(bob => bob.id === selectedBobToCopy)
      if (bobToCopy) {
        data = {
          beeldvorming: bobToCopy.beeldvorming,
          oordeelsvorming: bobToCopy.oordeelsvorming,
          besluitvorming: bobToCopy.besluitvorming,
        }
      }
    }
    // else: empty mode - data remains {}

    createBobAnalyseMutation.mutate(data)
  }

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setCreationMode('empty')
      setSelectedBobToCopy('')
    }
  }, [open])

  const canCreateEmpty = creationMode === 'empty'
  const canCreateCopy = creationMode === 'copy' && selectedBobToCopy
  const canCreate = canCreateEmpty || canCreateCopy

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Nieuwe BOB-GOGME Analyse</DialogTitle>
          <DialogDescription>
            Maak een nieuwe analyse aan. U kunt later bronnen toevoegen om suggesties te genereren.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Option 1: Create empty */}
          <div
            className={`space-y-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
              creationMode === 'empty'
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setCreationMode('empty')}
          >
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="empty"
                name="creation-mode"
                value="empty"
                checked={creationMode === 'empty'}
                onChange={() => setCreationMode('empty')}
                className="w-4 h-4 text-blue-600 cursor-pointer"
              />
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4 text-muted-foreground" />
                <label
                  htmlFor="empty"
                  className="flex-1 font-medium text-foreground cursor-pointer"
                >
                  Nieuwe lege analyse
                </label>
              </div>
            </div>
            <p className="ml-7 text-sm text-muted-foreground">
              Start met een lege analyse. U kunt daarna omgevingsbeelden of andere bronnen
              toevoegen om AI-suggesties te genereren.
            </p>
          </div>

          {/* Option 2: Copy existing */}
          <div
            className={`space-y-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
              creationMode === 'copy'
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setCreationMode('copy')}
          >
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="copy"
                name="creation-mode"
                value="copy"
                checked={creationMode === 'copy'}
                onChange={() => setCreationMode('copy')}
                className="w-4 h-4 text-blue-600 cursor-pointer"
              />
              <div className="flex items-center gap-2">
                <Copy className="h-4 w-4 text-muted-foreground" />
                <label
                  htmlFor="copy"
                  className="flex-1 font-medium text-foreground cursor-pointer"
                >
                  Kopieer bestaande analyse
                </label>
              </div>
            </div>

            {creationMode === 'copy' && (
              <div className="ml-7 space-y-2">
                <p className="text-sm text-muted-foreground">
                  Selecteer een bestaande analyse om te kopiëren
                </p>
                <Select
                  value={selectedBobToCopy}
                  onValueChange={setSelectedBobToCopy}
                  disabled={
                    createBobAnalyseMutation.isPending ||
                    !existingBobAnalyses ||
                    existingBobAnalyses.length === 0
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={
                        existingBobAnalyses && existingBobAnalyses.length > 0
                          ? 'Selecteer analyse'
                          : 'Geen analyses beschikbaar'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {existingBobAnalyses &&
                      existingBobAnalyses.length > 0 &&
                      existingBobAnalyses.map((bob, index) => {
                        const createdDate = new Date(bob.createdAt)
                        const formattedDateTime = formatDateTime(createdDate)
                        const analyseNumber = index + 1

                        return (
                          <SelectItem key={bob.id} value={bob.id} className="py-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm">
                                Strategie {analyseNumber} | {formattedDateTime}
                              </span>
                            </div>
                          </SelectItem>
                        )
                      })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {creationMode !== 'copy' && (
              <p className="ml-7 text-sm text-muted-foreground">
                Maak een kopie van een bestaande analyse als startpunt
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createBobAnalyseMutation.isPending}
          >
            {t('common.annuleren')}
          </Button>
          <Button
            onClick={handleCreateBob}
            disabled={createBobAnalyseMutation.isPending || !canCreate}
          >
            <Plus className="mr-2 h-4 w-4" />
            {createBobAnalyseMutation.isPending ? 'Aanmaken...' : 'Aanmaken'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
